import React from 'react';
import { Trans } from 'react-i18next';
import { call, put, select, race, take } from 'redux-saga/effects';
import type { SagaIterator } from 'redux-saga';
import { requestPuppy } from '@wix/da-http-client';
import { getCurrentGallection, getGallectionById, getGrusername } from '../selectors';
import type { Action } from 'redux-actions';
import { CopyDeviationsPayload, deselectAll } from '../actions';
import type { PapiRequestDaSharedGallectionFoldersCopyDeviations } from '@wix/da-papi-types';
import setGallectionCover from './setGallectionCover';
import updateGallectionSize from './updateGallectionSize';
import { isPremiumGallection, getPremiumGallectionType } from '../../utils/gallections';
import {
  pushGlobalConfirmationModalForMM,
  confirmYes,
  confirmNo,
} from '../../Modals/redux/actionCreators';
import { Warning } from '@wix/da-ds/pkg/Icons/Confirmations';
import { checkAndWarnIfDeviationsAreNotSupportedForPremium } from './helpers';
import { getGallectionType } from '../helpers';

export default function* (action: Action<CopyDeviationsPayload>): SagaIterator {
  const { deviations, targetFolderId } = action.payload;
  const {
    currentGallection,
    targetGallection,
    username,
  }: {
    currentGallection: ReturnType<typeof getCurrentGallection>;
    targetGallection: ReturnType<typeof getGallectionById>;
    username: string;
  } = yield select(state => ({
    currentGallection: getCurrentGallection(state),
    targetGallection: getGallectionById(state, targetFolderId),
    username: getGrusername(state),
  }));

  const type = getGallectionType(currentGallection);
  if (!currentGallection || !targetGallection || !type || type === 'stash') {
    return;
  }

  const isFromPremiumFolder = isPremiumGallection(currentGallection);
  const isToPremiumFolder = isPremiumGallection(targetGallection);
  if (!isFromPremiumFolder && isToPremiumFolder) {
    const hasUnsupportedDeviationSelected: ReturnType<
      typeof checkAndWarnIfDeviationsAreNotSupportedForPremium
    > = yield call(checkAndWarnIfDeviationsAreNotSupportedForPremium, deviations);
    if (hasUnsupportedDeviationSelected) {
      return;
    }

    const premiumType = getPremiumGallectionType(targetGallection);
    let confirmationTextTransKey: React.ReactNode;
    if (premiumType === 'paid') {
      confirmationTextTransKey = (
        <Trans i18nKey="mediapopup.confirmationPopup.moveDeviation.text.fromRegularToPaid">
          • Only your paying Supporters will have access to it.
          <br />• It will become locked for all your non-Supporters and will affect Collections,
          Groups, Profile Spotlights,{' '}
          <a href="https://www.deviantartsupport.com/en/article/what-happens-when-i-move-deviations-in-or-out-of-premium-galleries">
            and more
          </a>
          .<br />
          <br />
          Are you sure you want to move this deviation?
        </Trans>
      );
    } else {
      confirmationTextTransKey = (
        <Trans i18nKey="mediapopup.confirmationPopup.moveDeviation.text.fromRegularToWatchersOnly">
          • Only your watchers will have access to it.
          <br />• It will become locked for deviants who don’t watch you and will affect
          Collections, Groups, Profile Spotlights,{' '}
          <a href="https://www.deviantartsupport.com/en/article/what-happens-when-i-move-deviations-in-or-out-of-premium-galleries">
            and more
          </a>
          .<br />
          <br />
          Are you sure you want to move this deviation?
        </Trans>
      );
    }
    yield put(
      pushGlobalConfirmationModalForMM({
        titleTransKey: 'mediapopup.confirmationPopup.moveDeviation.title',
        textTransKey: confirmationTextTransKey,
        confirmBtnLabelTransKey: 'mediapopup.confirmationPopup.moveDeviation.confirm',
        cancelBtnLabelTransKey: 'popup.cancel',
        icon: <Warning />,
      })
    );
    const { no } = yield race({
      yes: take(confirmYes),
      no: take(confirmNo),
    });
    if (no) {
      return;
    }
  }

  yield put(deselectAll());

  yield call(setGallectionCover, {
    gallection: targetGallection,
    deviation: deviations[0],
    onlyIfMissing: true,
  });
  yield call(updateGallectionSize, targetFolderId, deviations.length);

  const deviationIds = deviations.map(deviation => deviation.deviationId);
  const params: PapiRequestDaSharedGallectionFoldersCopyDeviations = {
    username,
    type,
    target_folderid: targetFolderId,
    deviationids: deviationIds,
  };

  yield call(requestPuppy, {
    method: 'post',
    url: 'gallection/folders/copy_deviations',
    data: params,
  });
}
